import { toast } from "sonner";
import { Section, SectionHead, LockOverlay } from "./SectionPrimitives";
import { fmt$ } from "./reportUtils";

export default function RecommendationsSection({ hasAccess, recommendations, issues, copy_assets, fastest_wins, overall, proj90, missedLeads, loss, annLoss, handleUnlock, checkoutLoading }) {
  return (
    <>
      {/* 10. FULL RECOMMENDATIONS */}
      <Section id="recommendations">
        <SectionHead num="07" title="Your Complete Fix Plan" subtitle="Every issue, ranked by impact. Follow this in order." />
        {hasAccess ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(recommendations.length > 0 ? recommendations : issues).map((rec, i) => (
              <div key={i} style={{ display: "flex", gap: 14, padding: "16px 20px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: rec.priority === "high" ? "#B91C1C" : rec.priority === "medium" ? "#9A6700" : "#0D6E3F", color: "#fff", fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 }}>{rec.issue || rec.title}</h3>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: rec.priority === "high" ? "#FEE2E2" : rec.priority === "medium" ? "#FEF3C7" : "#D1FAE5", color: rec.priority === "high" ? "#B91C1C" : rec.priority === "medium" ? "#9A6700" : "#0D6E3F" }}>{(rec.priority || "medium").toUpperCase()}</span>
                  </div>
                  <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.7, margin: 0 }}>{rec.impact || rec.description}</p>
                </div>
              </div>
            ))}
          </div>
        ) : <LockOverlay onUnlock={handleUnlock} loading={checkoutLoading} loss={loss} />}
      </Section>

      {/* 11. TEMPLATES */}
      <TemplatesSection hasAccess={hasAccess} copy_assets={copy_assets} handleUnlock={handleUnlock} checkoutLoading={checkoutLoading} loss={loss} />

      {/* 12. 90-DAY ROADMAP */}
      <Section id="plan">
        <SectionHead num="08" title="Your 90-Day Roadmap" subtitle="Phased timeline. Follow this step-by-step." />
        {hasAccess ? (
          <div>
            {(fastest_wins.length > 0 ? fastest_wins : issues.slice(0, 8)).map((w, i) => (
              <div key={i} style={{ display: "flex", gap: 16, padding: "16px 20px", background: i % 2 === 0 ? "#F9FAFB" : "#fff", borderRadius: 8, marginBottom: 4 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: i < 2 ? "#B91C1C" : i < 5 ? "#9A6700" : "#0D6E3F", color: "#fff", fontWeight: 800, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 2 }}>{w.title || w.issue}</p>
                  <p style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.6 }}>{w.impact || ""}</p>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 20, background: "#D1FAE5", border: "1px solid #A7F3D0", borderRadius: 10, padding: "20px 24px", textAlign: "center" }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#0D6E3F" }}>Projected Score After 90 Days</p>
              <p style={{ fontSize: 32, fontWeight: 900, color: "#0D6E3F", margin: "6px 0" }}>{overall} → {proj90}</p>
              <p style={{ fontSize: 14, color: "#0D6E3F" }}>+{proj90 - overall} point improvement</p>
            </div>
          </div>
        ) : <LockOverlay onUnlock={handleUnlock} loading={checkoutLoading} loss={loss} />}
      </Section>

      {/* 13. MISSED CALLS CALCULATOR */}
      <Section id="missedcalls">
        <SectionHead title="Missed Lead Calculator" subtitle="What your invisibility costs in real customers." />
        {hasAccess ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
            <div style={{ background: "#FEF2F2", border: "2px solid #FECACA", borderRadius: 10, padding: 22, textAlign: "center" }}>
              <p style={{ fontSize: "clamp(26px, 5vw, 38px)", fontWeight: 900, color: "#B91C1C", lineHeight: 1 }}>{missedLeads}</p>
              <p style={{ fontSize: 14, color: "#991B1B", fontWeight: 600, marginTop: 6 }}>Leads missed per month</p>
            </div>
            <div style={{ background: "#FEF2F2", border: "2px solid #FECACA", borderRadius: 10, padding: 22, textAlign: "center" }}>
              <p style={{ fontSize: "clamp(22px, 4.5vw, 34px)", fontWeight: 900, color: "#B91C1C", lineHeight: 1, wordBreak: "break-word" }}>{fmt$(loss)}</p>
              <p style={{ fontSize: 14, color: "#991B1B", fontWeight: 600, marginTop: 6 }}>Revenue lost monthly</p>
            </div>
            <div style={{ background: "#FEE2E2", border: "2px solid #FECACA", borderRadius: 10, padding: 22, textAlign: "center" }}>
              <p style={{ fontSize: "clamp(22px, 4.5vw, 34px)", fontWeight: 900, color: "#7F1D1D", lineHeight: 1, wordBreak: "break-word" }}>{fmt$(annLoss)}</p>
              <p style={{ fontSize: 14, color: "#991B1B", fontWeight: 600, marginTop: 6 }}>Annual cost of inaction</p>
            </div>
          </div>
        ) : <LockOverlay onUnlock={handleUnlock} loading={checkoutLoading} loss={loss} />}
      </Section>

      {/* 14. GUARANTEE */}
      <Section id="guarantee">
        <SectionHead num="10" title="Our Performance Commitment" />
        {hasAccess ? (
          <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: 28, display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
            <div style={{ width: 88, height: 88, borderRadius: "50%", border: "3px solid #111827", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "#fff" }}>
              <p style={{ fontSize: 9, fontWeight: 800, color: "#4B5563", letterSpacing: ".06em" }}>RANKIFY</p>
              <p style={{ fontSize: 12, fontWeight: 900, color: "#111827" }}>GUARANTEE</p>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 8 }}>If We Manage Your Campaign — We Guarantee Results</h3>
              <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.7 }}>If your primary keywords aren't ranking <strong>Top 3 in Google Maps within 90 days</strong>, we continue working at no additional cost until they do. 94% of our clients achieve Top 3 within the first 90 days.</p>
            </div>
          </div>
        ) : <LockOverlay onUnlock={handleUnlock} loading={checkoutLoading} loss={loss} />}
      </Section>
    </>
  );
}

function TemplatesSection({ hasAccess, copy_assets, handleUnlock, checkoutLoading, loss }) {
  return (
    <Section id="templates">
      <SectionHead num="09" title="Copy-Paste Templates" subtitle="Written specifically for your business. No placeholders." />
      {hasAccess ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[["GBP Description", copy_assets?.gbp_description], ["Review Request (SMS)", copy_assets?.review_request_sms], ["Review Request (Email)", copy_assets?.review_request_email], ["Positive Review Response", copy_assets?.positive_review_response], ["Negative Review Response", copy_assets?.negative_review_response], ["Google Post", copy_assets?.google_post]]
            .filter(([, v]) => v).map(([label, raw], i) => {
              const text = typeof raw === "string" ? raw : (raw?.body ? `${raw.subject ? raw.subject + "\n\n" : ""}${raw.body}` : JSON.stringify(raw));
              return (
                <div key={i}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "#4B5563", marginBottom: 6, letterSpacing: ".04em" }}>{label.toUpperCase()}</p>
                  <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderLeft: "3px solid #111827", borderRadius: "0 10px 10px 0", padding: "16px 20px", position: "relative" }}>
                    <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{text}</p>
                    <button onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied!"); }}
                      data-pdf-hide="true"
                      style={{ position: "absolute", top: 12, right: 12, background: "#111827", color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer" }}>Copy</button>
                  </div>
                </div>
              );
            })}
        </div>
      ) : <LockOverlay onUnlock={handleUnlock} loading={checkoutLoading} loss={loss} />}
    </Section>
  );
}
